/**
 * JobsiteSelectorDropdown - Reusable dropdown for selecting jobsites in BOLT AI
 *
 * Features:
 * - Search/filter by jobsite name
 * - Click-outside to close
 * - Matches Bolt dark theme
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { MapPin, Globe, ChevronDown, Search, Check } from 'lucide-react';

export default function JobsiteSelectorDropdown({ jobsites = [], selectedId, onChange, className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);

  const selectedJobsite = useMemo(
    () => jobsites.find((j) => String(j._id) === String(selectedId)),
    [jobsites, selectedId]
  );

  const filteredJobsites = useMemo(() => {
    if (!searchQuery.trim()) return jobsites;
    const q = searchQuery.toLowerCase();
    return jobsites.filter((j) => (j.name || '').toLowerCase().includes(q));
  }, [jobsites, searchQuery]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (id) => {
    onChange?.(id);
    setIsOpen(false);
    setSearchQuery('');
  };

  const isGeneral = !selectedId;
  const displayName = isGeneral ? 'All Sites' : (selectedJobsite?.name || 'Select a jobsite...');

  return (
    <div className={`bolt-jobsite-selector ${className}`.trim()} ref={containerRef}>
      <button
        type="button"
        className="bolt-jobsite-trigger"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {isGeneral ? <Globe size={16} /> : <MapPin size={16} />}
        <span className="bolt-jobsite-trigger-text">{displayName}</span>
        <ChevronDown size={14} className={isOpen ? 'bolt-jobsite-chevron-open' : ''} />
      </button>
      {isOpen && (
        <div className="bolt-jobsite-dropdown">
          <div className="bolt-jobsite-search">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search jobsites..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
          </div>
          <div className="bolt-jobsite-list" role="listbox">
            {!searchQuery.trim() && (
              <button
                type="button"
                role="option"
                aria-selected={isGeneral}
                className={`bolt-jobsite-item ${isGeneral ? 'selected' : ''}`}
                onClick={() => handleSelect(null)}
              >
                <span className="bolt-jobsite-item-check">
                  {isGeneral && <Check size={14} />}
                </span>
                <Globe size={14} style={{ opacity: 0.6, marginRight: 4 }} />
                <span className="bolt-jobsite-item-text">All Sites (General)</span>
              </button>
            )}
            {filteredJobsites.length > 0 ? (
              filteredJobsites.map((jobsite) => {
                const id = String(jobsite._id);
                const isSelected = id === String(selectedId);
                return (
                  <button
                    key={id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`bolt-jobsite-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelect(jobsite._id)}
                  >
                    <span className="bolt-jobsite-item-check">
                      {isSelected && <Check size={14} />}
                    </span>
                    <span className="bolt-jobsite-item-text">{jobsite.name}</span>
                  </button>
                );
              })
            ) : (
              <div className="bolt-jobsite-empty">No jobsites match</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
